import { action, computed, makeAutoObservable, runInAction } from "mobx"
import {
    ExternalIntegrationType,
    MenuData,
    MenuType,
    MenuWithExtraData,
    QRCodesURL,
    ReportingDestinationType,
    TestOrder,
} from "../../../../services/menu/models";
import {
    createBookingMenu,
    createMenu,
    createMenuIntegration,
    getBookingMenus,
    getMenus,
    getTelegramReportingBindingUrl,
    updateMenu,
} from "../../../../services/menu";
import { ICreateMenuRequest, IUpdateMenuRequest, } from "../../../../services/menu/requestsResponses";
import { LocalizedString } from "../../../../services/general/models";
import MenuFormsStore, { MenuFormType } from "./forms";
import { localize } from "../../../../common/libs";
import { AliasedMenu } from "../../../../services/aliases/models";
import { createAlias, updateAlias } from "../../../../services/aliases";
import { getStats } from "../../../../services/stats";
import { Moment } from "moment";


class GeneralMenuStore {
    forms = new MenuFormsStore();
    qrCodesURLs: QRCodesURL[] = [];
    testOrders: TestOrder[] = [];
    bindToGroupUrl: string | null;
    bindToPrivateMessagesUrl: string | null;
    menusList: MenuWithExtraData[] = []
    bookingList: MenuWithExtraData[] = [];
    tablesQuantity: number;
    isLoaded: boolean = false;

    constructor() {
        makeAutoObservable(this);
    }


    @computed
    getMenuById(menuId: number, type: MenuType = MenuType.regular) {
        if(type === MenuType.booking.toLowerCase()){
            return this.bookingList.find(item => item.id === menuId);
        }else{
            return this.menusList.find(item => item.id === menuId);
        }
    }


    async fetchMenus(type: MenuType = MenuType.regular) {
        let response;
        if(type === MenuType.regular){
            response = await getMenus();
            runInAction(() => {
                this.menusList = response.menus;
            })
        }else{
            response = await getBookingMenus();
            runInAction(() => {
                this.bookingList = response.menus;
            })
        }
        runInAction(() => {
            this.tablesQuantity = response.tablesQuantity;
            if (this.forms.forms[MenuFormType.updateChatForm].current) {
                const menu = this.getMenuById(this.forms.forms[MenuFormType.updateChatForm].current.id, type);
                this.forms.forms[MenuFormType.updateChatForm].current = menu;
            }
            if (this.forms.forms[MenuFormType.createMenu].current) {
                const menu = this.getMenuById(this.forms.forms[MenuFormType.createMenu].current.id, type);
                this.forms.forms[MenuFormType.createMenu].current = menu;
            }
            if (this.forms.forms[MenuFormType.showChecklist].current) {
                const currentMenu = this.menusList.find(item => item.id === this.forms.forms["showChecklist"].current?.id);
                this.forms.showPopupForm(MenuFormType.showChecklist, currentMenu);
            }
        })
    }

    async loadDetailsStats(from: Moment, to: Moment, id: number) {
        return await getStats({
            from: from.toDate(),
            to: to.toDate(),
            menuId: id,
        });
    }

    async createMenu(data: MenuData, type: MenuType = MenuType.regular) {
        let res;
        if(type === MenuType.regular){
            const payload: ICreateMenuRequest = {
                data
            };
            res = await createMenu(payload);
        }else{
            const payload: ICreateMenuRequest = {
                data
            };
            res = await createBookingMenu(payload);

        }
        await this.fetchMenus(type);
        return res;
    }

    async updateMenu(menuId: number, data: MenuData) {
        const payload: IUpdateMenuRequest = {
            data,
            menuId: menuId
        };
        let res = await updateMenu(payload);
        await this.fetchMenus();
        return res;
    }

    @action
    setTestOrder(item: TestOrder) {
        this.testOrders = this.testOrders.concat(item);
    }

    @computed
    getTestOrder(id: number) {
        return this.testOrders.find((item) => item.menuId === id);
    }

    async ensureMenusLoaded() {
        if (!this.isLoaded) {
            await this.fetchMenus();
        }
    }

    searchForMenus(query: string, type: MenuType = MenuType.regular) {
        let list = [];
        if(type === MenuType.regular){
            list = this.menusList;
        }else{
            list = this.bookingList;
        }

        if (query.length === 0) return [...list];
        return list.filter(item => {
            const t = localize(item.data.name).toLowerCase().search(query.toLowerCase());
            return t >= 0;
        })
    }

    @action
    reset() {
        this.forms = new MenuFormsStore();
        this.menusList = [];
        this.bookingList = [];
    }




    async createAlias(params: {
        alias: string,
        aliasedMenus: AliasedMenu[],
        title: LocalizedString,
        description: LocalizedString,
        photosUrls: string[]
    }) {
        const res = await createAlias({
            alias: params.alias,
            data: {
                title: params.title,
                description: params.description,
                photosUrls: params.photosUrls,
                aliasedMenus: params.aliasedMenus
            }
        })
    }

    async updateAlias(params: {
        alias: string,
        aliasedMenus: AliasedMenu[],
        title: LocalizedString,
        description: LocalizedString,
        photosUrls: string[]
    }) {
        const res = await updateAlias({
            alias: params.alias,
            data: {
                title: params.title,
                description: params.description,
                photosUrls: params.photosUrls,
                aliasedMenus: params.aliasedMenus
            }
        })
    }

    @computed
    getBindToGroupUrl() {
        return this.bindToGroupUrl;
    }

    @computed
    getBindToPrivateMessagesUrl() {
        return this.bindToPrivateMessagesUrl;
    }

    async fetchTelegramReportingBindingUrl(id: number) {
        const response = await getTelegramReportingBindingUrl({
            menuId: id,
        });
        runInAction(() => {
            this.bindToGroupUrl = response.bindToGroupUrl;
            this.bindToPrivateMessagesUrl = response.bindToPrivateMessagesUrl;
        });
    }


    @computed
    getReportingDestinations(menuId: number, type: MenuType = MenuType.regular) {
        let menu: MenuWithExtraData | undefined;
        if(type === MenuType.regular){
            menu = this.menusList.find(item => item.id === menuId);
        }else{
            menu = this.bookingList.find(item => item.id === menuId);
        }
        if (menu) {
            return menu.data.reporting.destinations
        } else {
            return [];
        }
    }


    async createMenuWithIIkoIntegration(iikoData: {
        apiLogin: string,
        iikoOrganizationId: string,
        terminalId: string
    }, data: MenuData) {
        const menuResponse = await this.createMenu(data, MenuType.regular);
        const {menuId} = menuResponse;
        const externalIntegrationDetailsResponse = await createMenuIntegration({
            menuId,
            data: {
                type: ExternalIntegrationType.iikoCloud,
                apiLogin: iikoData.apiLogin,
                iikoOrganizationId: iikoData.iikoOrganizationId
            }
        });
        const updatedMenuData: MenuData = {
            ...data,
            externalIntegrationSettings: {
                isEnabled: true,
                descriptor: externalIntegrationDetailsResponse.externalMenuIntegrationDescriptor
            },
            reporting: {
                destinations: [
                    ...data.reporting.destinations,
                    {
                        type: ReportingDestinationType.iikoCloudTerminal,
                        filters: [],
                        terminalGroupId: iikoData.terminalId
                    }
                ]
            }
        };
        await this.updateMenu(menuId, updatedMenuData);
        await this.fetchMenus();
        return menuResponse
    }


}

export default GeneralMenuStore;
